import { LitElement, html, css, ref, createRef, utils } from '@hh/global.js'
const { throttle } = utils

class HHHistoryGrid extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    _clickMove: {
      state: true,
    },
  }

  $container = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1

    this.throttleScroll = throttle((e) => {
      this.onScroll(e)
    }, 300)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="moment"]'))
    this._total = this.items.length

    this.yearElements = Array.from(this.querySelectorAll('[slot="year"]'))
    this.yearsUsed = this.items.map((item) => item.getAttribute('year'))

    this._clickMove = false
    window.addEventListener('keydown', this.onModalKeydown.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('keydown', this.onModalKeydown)
  }

  firstUpdated() {
    this.$container?.value?.addEventListener(
      'scroll',
      this.throttleScroll.bind(this)
    )

    this.hideUnusedYears()
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      if (this._clickMove) return
      this.updateYear()
    }
  }

  onScroll(e) {
    this.updateUI(e)
  }

  onPaginateClick(e) {
    e.preventDefault()
    const button = e.target.closest('button')

    const slideScrollPosition =
      button.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  onYearClick(e) {
    e.preventDefault()
    this._clickMove = true

    const { target } = e
    const year = target.getAttribute('year')
    this.moveToYear(year)
    this.setActiveYear(target)

    setTimeout(() => (this._clickMove = false), 1000)
  }

  updateUI(e) {
    if (!this._total) return

    this.slideStopPoints.every((stopPoint, index) => {
      if (e.target.scrollLeft <= stopPoint - 100) {
        this._current = index + 1
        return false
      }
      return true
    })

    this._current === 1
      ? this.prevButton?.setAttribute('disabled', true)
      : this.prevButton?.removeAttribute('disabled')

    this._current === this._total
      ? this.nextButton?.setAttribute('disabled', true)
      : this.nextButton?.removeAttribute('disabled')
  }

  updateYear() {
    const currentMoment = this.items[this._current - 1]
    const currentYear = currentMoment?.getAttribute('year')

    const currentYearButton = this.getYearByAttr(currentYear)
    this.setActiveYear(currentYearButton)
  }

  moveToYear(year) {
    const yearMoment = this.items.find(
      (item) => item.getAttribute('year') === year
    )
    if (!yearMoment) return

    const yearMomentScrollPosition = this.calcScrollPosition(yearMoment)
    this.$container?.value?.scrollTo({
      left: yearMomentScrollPosition,
    })

    yearMoment.focus({ preventScroll: true })
  }

  calcScrollPosition(currentMoment) {
    const currentMomentIndex = this.items.indexOf(currentMoment)
    const currentSlideWidth = this.slideWidths.splice(0, currentMomentIndex)
    const currentMomentScrollPosition = currentSlideWidth.reduce(
      (acc, width) => acc + width,
      0
    )
    return currentMomentScrollPosition
  }

  moveToMoment(index) {
    const currentMoment = this.items[index]
    const currentMomentScrollPosition = index * currentMoment.clientWidth
    this.$container?.value?.scrollTo({
      left: currentMomentScrollPosition,
    })
  }

  setActiveYear(button) {
    if (!button) return
    this.removeActiveYear()
    button.setAttribute('disabled', true)
    button.setAttribute('aria-current', true)
  }

  removeActiveYear() {
    this.yearSlots.forEach((slot) => {
      const button = slot.querySelector('button')
      if (!button) return
      button.removeAttribute('disabled')
      button.removeAttribute('aria-current')
    })
  }

  hideUnusedYears() {
    this.yearElements.forEach((yearElement) => {
      const yearValue = yearElement.getAttribute('year')
      if (!yearValue) return
      if (this.yearsUsed.includes(yearValue)) return
      yearElement.setAttribute('hidden', true)
    })
  }

  /* Video Modal */
  onVideoLinkClick(e) {
    e.preventDefault()
    scrollLock.disablePageScroll(this.videoModal)

    const videoUrl = e.target.getAttribute('video-url')
    const videoPoster = e.target.getAttribute('video-poster')

    if (videoUrl) {
      this.videoModal.innerHTML = this.videoTemplate(videoUrl, videoPoster)
      const videoHTML = this.videoModal.querySelector('video')
      videoHTML.addEventListener('canplay', () => {
        videoHTML.play()
      })
    } else {
      console.warn('No video URL or YouTube ID found')
    }
  }

  onModalClose() {
    scrollLock.enablePageScroll(this.videoModal)
    this.videoModal.innerHTML = ''
  }

  onModalClickOutside(e) {
    if (e.target !== this.hhModal) return
    this.hhModal.close(e)
    this.onModalClose()
  }

  onModalKeydown(e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return
    this.hhModal.close(e)
    this.onModalClose()
  }

  videoTemplate(source, poster = '') {
    if (poster) {
      return `<video controls playsinline src="${source}" poster="${poster}"></video>`
    } else {
      return `<video controls playsinline src="${source}"></video>`
    }
  }

  /* Getters */
  getYearByAttr(year) {
    return this.querySelector(`[slot="year"] button[year="${year}"]`) ?? null
  }

  get videoModal() {
    return this.querySelector('.js-video-modal') ?? null
  }

  get hhModal() {
    return this.querySelector('hh-modal') ?? null
  }

  get prevButton() {
    const slot = this.shadowRoot.querySelector('slot[name="pagination-prev"]')
    return slot.assignedElements({ flatten: true })[0] ?? null
  }

  get nextButton() {
    const slot = this.shadowRoot.querySelector('slot[name="pagination-next"]')
    return slot.assignedElements({ flatten: true })[0] ?? null
  }

  get yearSlots() {
    const slot = this.shadowRoot.querySelector('slot[name="year"]')
    return slot.assignedElements({ flatten: true }) ?? []
  }

  get slideWidths() {
    return this.items.map((item) => {
      const { width } = item.getBoundingClientRect()
      return width
    })
  }

  get slideStopPoints() {
    return this.slideWidths.reduce((acc, width) => {
      const last = acc[acc.length - 1] ?? 0
      acc.push(last + width)
      return acc
    }, [])
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div class="wrapper">
        <slot name="image"></slot>
        <div class="content">
          <slot name="title"></slot>
          <div class="timeline">
            <ul class="years">
              <slot name="year" @click="${this.onYearClick}"></slot>
            </ul>
            <div class="moments-wrapper">
              <ul class="moments" ${ref(this.$container)}>
                <slot
                  name="moment"
                  @openVideoModal=${this.onVideoLinkClick}
                ></slot>
              </ul>
              <slot
                name="pagination-prev"
                @click="${this.onPaginateClick}"
              ></slot>
              <slot
                name="pagination-next"
                @click="${this.onPaginateClick}"
              ></slot>
            </div>
          </div>
        </div>

        <div class="container">
          <slot
            name="video-modal"
            @modal-close=${this.onModalClose}
            @click=${this.onModalClickOutside}
            @keydown=${this.onModalKeydown}
          ></slot>
        </div>
      </div>`
  }

  static styles = css`
    :host {
      max-width: unset;
      display: block;
      box-sizing: border-box;
    }

    .wrapper {
      display: grid;
    }

    @media screen and (min-width: 750px) {
      .wrapper {
        grid-template-columns: 2fr 3fr;
        column-gap: 1rem;
      }
    }

    .container {
      width: 100%;
      max-width: 140rem;
      margin: 0 auto;
      box-sizing: border-box;
      padding: 0 1rem;
    }

    .moments-wrapper {
      position: relative;
    }

    .content {
      overflow-x: hidden;
    }

    .years,
    .moments {
      display: flex;
      list-style: none;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      scroll-padding-left: 1rem;
    }

    .years {
      column-gap: 2rem;
      padding: 0 1rem 1rem;
    }

    @media screen and (min-width: 750px) {
      .years {
        padding: 0 1rem 0;
        column-gap: 4rem;
      }
    }

    @media screen and (max-width: 1023px) {
      .years {
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      .years::-webkit-scrollbar {
        display: none;
      }
    }

    .moments {
      column-gap: 1rem;
      margin: 2.8rem 0 2rem;
      padding: 0 1rem;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .moments::-webkit-scrollbar {
      display: none;
    }

    @media screen and (min-width: 750px) {
      .moments {
        padding: 0 1rem 0 0;
        margin: 0.8rem 0 2rem;
      }
    }

    ::slotted([slot='year']) {
      padding: 0.2rem 0;
    }
  `
}

customElements.define('hh-history-grid', HHHistoryGrid)
